import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Flag, PawPrint, Star, Trash2, Undo2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatDate, formatMoney, formatNumber, relativeTime } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Separator,
  Skeleton,
} from "@/components/ui/primitives";
import { ConfirmDialog } from "@/components/data/ConfirmDialog";
import { LISTING_STATUS_BADGE } from "@/features/users/shared";

export const ListingDetailPage = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);

  const [action, setAction] = React.useState<
    null | "remove" | "restore" | "feature" | "unfeature" | "flag"
  >(null);

  const detail = useQuery({
    queryKey: ["listings", id],
    queryFn: () => api.get<any>(`/listings/${id}`),
    enabled: Boolean(id),
  });

  const mutate = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.postWithMessage(`/listings/${id}${path}`, body),
    onSuccess: ({ message }) => {
      toast.success(message);
      setAction(null);
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      void queryClient.invalidateQueries({ queryKey: ["moderation"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Action failed"),
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <EmptyState
        icon={PawPrint}
        title="Could not load this listing"
        description={detail.error instanceof Error ? detail.error.message : undefined}
        action={
          <Button variant="outline" onClick={() => navigate("/listings")}>
            Back to listings
          </Button>
        }
      />
    );
  }

  const { listing, moderation } = detail.data;
  const removed = listing.admin_status === "removed";
  const cfg = LISTING_STATUS_BADGE[listing.admin_status];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to="/listings" aria-label="Back to listings">
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <p className="text-sm text-muted-foreground">Listings</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-lg font-semibold">{listing.name}</h1>
                    <Badge variant={cfg?.variant ?? "secondary"}>
                      {cfg?.label ?? listing.admin_status}
                    </Badge>
                    {listing.is_featured && (
                      <Badge variant="warning">
                        <Star className="h-3 w-3" aria-hidden />
                        Featured
                      </Badge>
                    )}
                    {listing.moderation_state === "flagged" && (
                      <Badge variant="destructive">Flagged</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[listing.breed, listing.type, listing.gender, listing.dob]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <p className="numeric mt-1 text-xl font-semibold">
                    {formatMoney(listing.price)}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {can("listings:feature") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setAction(listing.is_featured ? "unfeature" : "feature")
                      }
                    >
                      <Star aria-hidden />
                      {listing.is_featured ? "Unfeature" : "Feature"}
                    </Button>
                  )}
                  {can("listings:flag") && listing.moderation_state !== "flagged" && (
                    <Button variant="outline" size="sm" onClick={() => setAction("flag")}>
                      <Flag aria-hidden />
                      Flag
                    </Button>
                  )}
                  {removed
                    ? can("listings:restore") && (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => setAction("restore")}
                        >
                          <Undo2 aria-hidden />
                          Restore
                        </Button>
                      )
                    : can("listings:remove") && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setAction("remove")}
                        >
                          <Trash2 aria-hidden />
                          Remove
                        </Button>
                      )}
                </div>
              </div>

              {listing.removal_reason && (
                <p className="mt-3 rounded border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-xs text-destructive">
                  Removed {formatDate(listing.removed_at)}: {listing.removal_reason}
                </p>
              )}

              {listing.description && (
                <>
                  <Separator className="my-4" />
                  <p className="whitespace-pre-wrap text-sm">{listing.description}</p>
                </>
              )}
            </CardContent>
          </Card>

          {listing.photos?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Photos ({listing.photos.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {listing.photos.map((p: string, i: number) => (
                    <a
                      key={i}
                      href={p}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="overflow-hidden rounded-lg border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <img
                        src={p}
                        alt={`Photo ${i + 1} of ${listing.name}`}
                        loading="lazy"
                        className="h-28 w-full object-cover transition-transform hover:scale-105"
                      />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {listing.reports?.length > 0 && (
            <Card className="border-destructive/30">
              <CardHeader>
                <CardTitle>Reports ({listing.reports.length})</CardTitle>
                <CardDescription>
                  Resolve these from the moderation queue so the listing and every
                  report move together.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {listing.reports.map((r: any) => (
                    <li key={r.id} className="flex items-start justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm">{r.reason}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.reporter?.name ?? "A user"} · {relativeTime(r.created_at)}
                        </p>
                      </div>
                      <Badge
                        variant={r.status === "open" ? "destructive" : "secondary"}
                      >
                        {r.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" size="sm" className="mt-3" asChild>
                  <Link to="/moderation?tab=reports">Open moderation queue</Link>
                </Button>
              </CardContent>
            </Card>
          )}

          {moderation?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Content moderation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {moderation.map((m: any) => (
                    <li key={m.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm">{m.entity_type}</p>
                        <p className="text-xs text-muted-foreground">
                          {m.ai_provider ?? "pending"}
                          {m.ai_score != null && ` · score ${m.ai_score.toFixed(2)}`}
                          {m.decision_note && ` · ${m.decision_note}`}
                        </p>
                      </div>
                      <Badge
                        variant={
                          m.status === "approved"
                            ? "success"
                            : m.status === "rejected"
                              ? "destructive"
                              : "warning"
                        }
                      >
                        {m.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Breeder</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {listing.breeder ? (
                <>
                  <Link
                    to={`/breeders/${listing.breeder.id}`}
                    className="font-medium hover:underline"
                  >
                    {listing.breeder.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {listing.breeder.city ?? "—"}
                    {listing.breeder.verified && " · verified"}
                    {listing.breeder.is_suspended && " · suspended"}
                  </p>
                  {listing.breeder.user?.id && (
                    <>
                      <Separator />
                      <Link
                        to={`/users/${listing.breeder.user.id}`}
                        className="text-xs text-primary hover:underline"
                      >
                        Open owner account
                      </Link>
                    </>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">No breeder linked</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Engagement</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-border text-sm">
                <li className="flex justify-between py-2">
                  <span className="text-muted-foreground">Contacts unlocked</span>
                  <span className="numeric">
                    {formatNumber(listing._count?.contacts_unlocked ?? 0)}
                  </span>
                </li>
                <li className="flex justify-between py-2">
                  <span className="text-muted-foreground">Saved</span>
                  <span className="numeric">
                    {formatNumber(listing._count?.favorites ?? 0)}
                  </span>
                </li>
                <li className="flex justify-between py-2">
                  <span className="text-muted-foreground">Messages</span>
                  <span className="numeric">
                    {formatNumber(listing._count?.messages ?? 0)}
                  </span>
                </li>
                <li className="flex justify-between py-2">
                  <span className="text-muted-foreground">Listed</span>
                  <span>{formatDate(listing.created_at)}</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {(listing.video_url || listing.health_cert_url) && (
            <Card>
              <CardHeader>
                <CardTitle>Attachments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {listing.video_url && (
                  <a
                    href={listing.video_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block text-sm text-primary hover:underline"
                  >
                    Video
                  </a>
                )}
                {listing.health_cert_url && (
                  <a
                    href={listing.health_cert_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block text-sm text-primary hover:underline"
                  >
                    Health certificate
                  </a>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={action === "remove"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`Remove “${listing.name}”?`}
        description="It disappears from the app immediately. You can restore it later."
        confirmLabel="Remove listing"
        variant="destructive"
        requireReason
        loading={mutate.isPending}
        onConfirm={(reason) => mutate.mutate({ path: "/remove", body: { reason } })}
      />

      <ConfirmDialog
        open={action === "restore"}
        onOpenChange={(o) => !o && setAction(null)}
        title={`Restore “${listing.name}”?`}
        description="It becomes visible in the app again."
        confirmLabel="Restore listing"
        variant="success"
        loading={mutate.isPending}
        onConfirm={() => mutate.mutate({ path: "/restore" })}
      />

      <ConfirmDialog
        open={action === "flag"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Flag this listing for review?"
        description="It is marked for moderation and hidden from browse until reviewed."
        confirmLabel="Flag listing"
        variant="destructive"
        requireReason
        loading={mutate.isPending}
        onConfirm={(reason) => mutate.mutate({ path: "/flag", body: { reason } })}
      />

      <ConfirmDialog
        open={action === "feature" || action === "unfeature"}
        onOpenChange={(o) => !o && setAction(null)}
        title={action === "feature" ? "Feature this listing?" : "Remove from featured?"}
        description={
          action === "feature"
            ? "It is surfaced more prominently in the app for the next 30 days."
            : "It returns to normal ranking."
        }
        confirmLabel={action === "feature" ? "Feature" : "Unfeature"}
        loading={mutate.isPending}
        onConfirm={() =>
          mutate.mutate({
            path: "/feature",
            body:
              action === "feature"
                ? { featured: true, days: 30 }
                : { featured: false },
          })
        }
      />
    </div>
  );
};
